import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, CameraIcon, CheckIcon, LayoutGridIcon, FileTextIcon, ListChecksIcon, SendIcon, SquareIcon } from 'lucide-react';
import { ControlPointStatus, ControlSectionKind, QualityReport } from '../types/quality';
import { ControlTab } from '../components/quality/ControlTab';
import { DetailsTab } from '../components/quality/DetailsTab';
import { PhotosTab } from '../components/quality/PhotosTab';
import { ReportPreview } from '../components/quality/ReportPreview';
import { getStats } from '../utils/quality';
import { ProgressRing } from '../components/quality/ProgressRing';
interface QualityReportDetailProps {
  report: QualityReport;
  onBack: () => void;
  onChange: (report: QualityReport) => void;
}
type Tab = 'details' | 'control' | 'photos' | 'report';
const tabs: {
  id: Tab;
  label: string;
  icon: typeof FileTextIcon;
}[] = [{
  id: 'details',
  label: 'Detaljer',
  icon: FileTextIcon
}, {
  id: 'control',
  label: 'Kontrol',
  icon: ListChecksIcon
}, {
  id: 'photos',
  label: 'Fotos',
  icon: CameraIcon
}, {
  id: 'report',
  label: 'Rapport',
  icon: SendIcon
}];
const EASE = [0.23, 1, 0.32, 1] as const;
export function QualityReportDetail({
  report: savedReport,
  onBack,
  onChange
}: QualityReportDetailProps) {
  const [tab, setTab] = useState<Tab>('details');
  const [isSplit, setIsSplit] = useState(true);

  /**
   * Edits are held in a local draft so typing stays responsive, then written
   * back shortly after the user stops — one save instead of one per keystroke.
   */
  const [draft, setDraft] = useState(savedReport);
  const [isSaving, setIsSaving] = useState(false);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isDirty = useRef(false);

  // Adopt a different report, or pick up remote changes while idle.
  useEffect(() => {
    if (!isDirty.current) setDraft(savedReport);
  }, [savedReport]);
  useEffect(() => {
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, []);
  const onChangeDraft = (next: QualityReport) => {
    setDraft(next);
    isDirty.current = true;
    setIsSaving(true);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      onChange(next);
      isDirty.current = false;
      setIsSaving(false);
    }, 500);
  };
  const report = draft;
  const stats = getStats(report);
  const setPointStatus = (pointId: string, status: ControlPointStatus) => {
    onChangeDraft({
      ...report,
      sections: report.sections.map(section => ({
        ...section,
        points: section.points.map(point => point.id === pointId ? {
          ...point,
          status
        } : point)
      }))
    });
  };
  const savedLabel = isSaving ? 'Gemmer…' : 'Gemt';
  const renamePoint = (pointId: string, title: string) => {
    onChangeDraft({
      ...report,
      sections: report.sections.map(section => ({
        ...section,
        points: section.points.map(point => point.id === pointId ? {
          ...point,
          title
        } : point)
      }))
    });
  };
  const deletePoint = (pointId: string) => {
    onChangeDraft({
      ...report,
      sections: report.sections.map(section => ({
        ...section,
        points: section.points.filter(point => point.id !== pointId)
      })),
      photos: report.photos.map(photo => photo.controlPointId === pointId ? {
        ...photo,
        controlPointId: undefined
      } : photo)
    });
  };
  const addPoint = (sectionId: string, title: string) => {
    onChangeDraft({
      ...report,
      sections: report.sections.map(section => section.id === sectionId ? {
        ...section,
        points: [...section.points, {
          id: `kp-${Date.now()}`,
          title,
          status: 'pending' as ControlPointStatus,
          requiresPhoto: true
        }]
      } : section)
    });
  };
  const renameSection = (sectionId: string, title: string) => {
    onChangeDraft({
      ...report,
      sections: report.sections.map(section => section.id === sectionId ? {
        ...section,
        title
      } : section)
    });
  };
  const addSection = (title: string, kind: ControlSectionKind) => {
    onChangeDraft({
      ...report,
      sections: [...report.sections, {
        id: `sec-${Date.now()}`,
        title,
        kind,
        points: []
      }]
    });
  };
  const deleteSection = (sectionId: string) => {
    const section = report.sections.find(s => s.id === sectionId);
    if (!section || !window.confirm(`Slet "${section.title}" og alle dens kontrolpunkter?`)) return;
    const removedIds = section.points.map(p => p.id);
    onChangeDraft({
      ...report,
      sections: report.sections.filter(s => s.id !== sectionId),
      photos: report.photos.map(photo => photo.controlPointId && removedIds.includes(photo.controlPointId) ? {
        ...photo,
        controlPointId: undefined
      } : photo)
    });
  };
  const assignPhoto = (photoId: string, pointId: string | undefined) => {
    onChangeDraft({
      ...report,
      photos: report.photos.map(photo => photo.id === photoId ? {
        ...photo,
        controlPointId: pointId
      } : photo)
    });
  };

  // The report tab already shows the preview, so splitting it would duplicate.
  const showPreview = isSplit && tab !== 'report';
  const editor = <AnimatePresence mode="wait" data-id="element-2255" data-mp="1ongkqisanruu">
      <motion.div key={tab} initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.18,
      ease: EASE
    }} data-id="element-2256" data-mp="1oo8edo2bzfih">
        {tab === 'details' && <DetailsTab report={report} onToggleVisibility={() => onChangeDraft({
        ...report,
        visibleToCustomer: !report.visibleToCustomer
      })} onDescriptionChange={description => onChangeDraft({
        ...report,
        description
      })} onResponsibleChange={responsible => onChangeDraft({
        ...report,
        responsible
      })} onCompanyChange={nextCompany => onChangeDraft({
        ...report,
        company: nextCompany
      })} onChange={changes => onChangeDraft({
        ...report,
        ...changes
      })} data-id="element-2257" data-mp="1jnkoj5pu94pu" />}
        {tab === 'control' && <ControlTab report={report} onSetPointStatus={setPointStatus} onRenamePoint={renamePoint} onDeletePoint={deletePoint} onAddPoint={addPoint} onRenameSection={renameSection} onDeleteSection={deleteSection} onAddSection={addSection} data-id="element-2258" data-mp="19im9b6uegt9w" />}
        {tab === 'photos' && <PhotosTab report={report} onAssignPhoto={assignPhoto} data-id="element-2259" data-mp="24tis2444jkdh" />}
        {tab === 'report' && <ReportPreview report={report} data-id="element-2260" data-mp="29r2gtxyg7fjo" />}
      </motion.div>
    </AnimatePresence>;
  return <div data-id="element-2261" data-mp="2eqndu2u25dtr">
      <button onClick={onBack} className="mb-5 inline-flex items-center gap-2 text-sm font-medium text-stone-500 transition-colors hover:text-stone-900" data-id="element-2262" data-mp="2epvsrly36yhq">
        <ArrowLeftIcon className="h-4 w-4" data-id="element-2263" data-mp="2oh9nlgf3fa4j" />
        Tilbage
      </button>

      {/* Header */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4" data-id="element-2264" data-mp="2shsijjca8way">
        <div className="flex min-w-0 items-center gap-4" data-id="element-2265" data-mp="2xhevdro78aj8">
          <ProgressRing percent={stats.percent} size={48} data-id="element-2266" data-mp="2xhy3kgo1rkk2" />
          <div className="min-w-0" data-id="element-2267" data-mp="32h19s4frjwtm">
            <div className="flex flex-wrap items-center gap-2.5" data-id="element-2268" data-mp="32fzkow1wx9sw">
              <h1 className="text-2xl font-bold tracking-tight text-stone-900" data-id="element-2269" data-mp="133x8m6vmbhp9">
                {report.reportId}
              </h1>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-stone-100 px-2.5 py-1 text-xs font-medium text-stone-600" data-id="element-2270" data-mp="1830ajigax7t0">
                <span className={`h-1.5 w-1.5 rounded-full ${report.status === 'Åben' ? 'bg-emerald-500' : 'bg-stone-400'}`} data-id="element-2271" data-mp="350xjgccautyo" />
                {report.status}
              </span>
            </div>
            <p className="mt-0.5 truncate text-sm text-stone-500" data-id="element-2272" data-mp="3erir0p9yb8tq">
              {report.customer.name} · {stats.completedPoints}/
              {stats.totalPoints} kontrolpunkter
            </p>
          </div>
        </div>

        <div className="flex flex-shrink-0 items-center gap-2" data-id="element-2273" data-mp="30zccf7mg59we">
          <span aria-live="polite" className={`inline-flex items-center gap-1.5 text-xs font-medium ${isSaving ? 'text-stone-400' : 'text-emerald-600'}`} data-id="element-2274" data-mp="310x6kw4umqtq">
            {!isSaving && <CheckIcon className="h-3.5 w-3.5" data-id="element-2275" data-mp="ba0maqph6hxj" />}
            {savedLabel}
          </span>
          <button onClick={() => setIsSplit(!isSplit)} aria-pressed={isSplit} className={`hidden items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors xl:inline-flex ${isSplit ? 'border-brand-200 bg-brand-50 text-brand-700' : 'border-stone-200 bg-white text-stone-600 hover:bg-stone-50'}`} data-id="element-2276" data-mp="bcczyro7a64s">
            {isSplit ? <LayoutGridIcon className="h-4 w-4" data-id="element-2277" data-mp="11lc3r6wk4ol8" /> : <SquareIcon className="h-4 w-4" data-id="element-2278" data-mp="16jnerkyl9551" />}
            Delt visning
          </button>
          <button className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-700" data-id="element-2279" data-mp="ss8vre4ol3xz">
            <SendIcon className="h-4 w-4" data-id="element-2280" data-mp="wqy6eyyc47dx" />
            Send til kunde
          </button>
        </div>
      </div>

      {/* Title field */}
      <div className="mb-6 flex items-center gap-3 rounded-2xl border border-brand-200 bg-brand-50/60 p-3" data-id="element-2281" data-mp="1p9va3gw1qrao">
        <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-brand-600 text-white" data-id="element-2282" data-mp="3lu7u2uj21n10">
          <ListChecksIcon className="h-5 w-5" data-id="element-2283" data-mp="3qrr8bufr5zsy" />
        </span>
        <label className="min-w-0 flex-1" data-id="element-2284" data-mp="3bvs10m0gw3ov">
          <span className="block text-[11px] font-semibold uppercase tracking-wider text-brand-700" data-id="element-2285" data-mp="3buqqcind5j7o">
            Titel på kvalitetssikringen
          </span>
          <input value={report.title} onChange={e => onChangeDraft({
          ...report,
          title: e.target.value
        })} className="mt-0.5 w-full border-none bg-transparent p-0 text-base font-semibold text-stone-900 outline-none placeholder:text-stone-400" placeholder="Hvad handler opgaven om?" data-id="element-2286" data-mp="332eycsclpnpl" />
        </label>
      </div>

      {/* Tabs */}
      <div role="tablist" aria-label="Kvalitetssikring sektioner" className="mb-6 flex gap-1 overflow-x-auto rounded-xl border border-stone-200 bg-white p-1" data-id="element-2287" data-mp="2k5411daj54cs">
        {tabs.map(item => {
        const Icon = item.icon;
        const isActive = tab === item.id;
        return <button key={item.id} role="tab" aria-selected={isActive} onClick={() => setTab(item.id)} className={`relative flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-lg px-4 py-2.5 text-sm font-medium transition-colors ${isActive ? 'text-brand-700' : 'text-stone-500 hover:text-stone-900'}`} data-id="element-2288" data-mp="20jvh5fhc1ng5">
              {isActive && <motion.span layoutId="qaTab" className="absolute inset-0 -z-10 rounded-lg bg-brand-50" transition={{
            duration: 0.22,
            ease: EASE
          }} data-id="element-2289" data-mp="2x7ibn7uuivqy" />}
              <Icon className="h-4 w-4" data-id="element-2290" data-mp="327msiyzo1shp" />
              {item.label}
            </button>;
      })}
      </div>

      {showPreview ? <div className="grid grid-cols-1 items-start gap-6 xl:grid-cols-2" data-id="element-2291" data-mp="1zvji475iilu9">
          <div className="min-w-0" data-id="element-2292" data-mp="23us7z0xflzkj">{editor}</div>
          <div className="min-w-0 xl:sticky xl:top-24" data-id="element-2293" data-mp="23th3t002qpsw">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-stone-400" data-id="element-2294" data-mp="28tlnt006y2nv">
              Sådan ser kunden rapporten
            </p>
            <div className="max-h-[calc(100vh-11rem)] overflow-y-auto rounded-2xl" data-id="element-2295" data-mp="2ikgb87p6b8aw">
              <ReportPreview report={report} data-id="element-2296" data-mp="2imss0hj73cmd" />
            </div>
          </div>
        </div> : editor}
    </div>;
}